import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'

// ---------------------------------------------------------------------------
// Types — mirror the `meiuca_app/v1` class-packages DTOs
// ---------------------------------------------------------------------------

/** Item of `GET /training-centers/{id}/class-packages` (catalog for sale). */
export interface ClassPackageForSale {
  id: string
  name: string
  classCount: number
  priceCents: number
  validityDays: number
}

export interface ClassPackageCatalog {
  packages: ClassPackageForSale[]
  /** Whether the center can actually sell through the app (online Pix). */
  pixAvailable: boolean
}

export type ClassPackagePurchaseStatus = 'pending_payment' | 'active' | 'expired' | 'canceled'

export interface ClassPackagePurchase {
  id: string
  trainingCenterId: string
  name: string
  classCount: number
  priceCents: number
  validityDays: number
  status: ClassPackagePurchaseStatus
  paidAt?: string | null
  expiresAt?: string | null
}

export interface ClassPackagePurchasePix {
  paymentId: string
  payload?: string | null
  encodedImage?: string | null
  expirationDate?: string | null
}

export interface ClassPackagePurchaseDetail {
  purchase: ClassPackagePurchase
  pix?: ClassPackagePurchasePix | null
}

class ClassPackageService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '',
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  /** The center's catalog as offered to students (active packages only). */
  async listPackagesForSale(trainingCenterId: string): Promise<ClassPackageCatalog> {
    const response = await this.get<{ data: ClassPackageCatalog }>(
      `/training-centers/${encodeURIComponent(trainingCenterId)}/class-packages`
    )
    return response.data.data
  }

  /**
   * Buys a package with on-the-spot Pix. Buying is just buying — when the
   * flow started from a class, the CLIENT enrolls through the regular
   * self-enroll once the purchase activates.
   */
  async purchase(trainingCenterId: string, packageId: string): Promise<ClassPackagePurchase> {
    const response = await this.post<{ data: { purchase: ClassPackagePurchase } }>(
      '/me/class-packages',
      { trainingCenterId, packageId }
    )
    return response.data.data.purchase
  }

  /**
   * Polling endpoint of a purchase — Pix while pending, `active` once paid,
   * `canceled` when the Pix expired.
   */
  async getPurchase(purchaseId: string): Promise<ClassPackagePurchaseDetail> {
    const response = await this.get<{ data: ClassPackagePurchaseDetail }>(
      `/me/class-packages/${encodeURIComponent(purchaseId)}`
    )
    return response.data.data
  }
}

export const classPackageService = new ClassPackageService()
